/**
* Main Application Module
* Coordinates all modules and handles application initialization
*/

// Global error handler to filter out browser extension and third-party service errors
window.addEventListener('error', (event) => {
    const errorMessage = event.message || '';
    const errorFilename = event.filename || '';
    
    // Filter out errors from browser extensions (content scripts)
    if (errorFilename && (
        errorFilename.includes('content.js') ||
        errorFilename.includes('extension://') ||
        errorFilename.includes('chrome-extension://') ||
        errorFilename.includes('moz-extension://') ||
        errorFilename.includes('rokt')
    )) {
        // Silently ignore browser extension and third-party service errors
        event.preventDefault();
        return false;
    }
    
    // Filter out errors related to checkoutUrls (common in shopping extensions)
    if (errorMessage.includes('checkoutUrls') || 
        errorMessage.includes('checkout') && errorMessage.includes('undefined')) {
        // Silently ignore browser extension checkout-related errors
        event.preventDefault();
        return false;
    }
    
    // Filter out CSP violations from third-party services
    if (errorMessage && (
        errorMessage.includes('Content Security Policy') ||
        errorMessage.includes('CSP') ||
        errorMessage.includes('rokt')
    )) {
        // Silently ignore CSP violations from third-party services
        event.preventDefault();
        return false;
    }
    
    // Allow other errors to be logged normally
    return true;
});

// Handle unhandled promise rejections from extensions and third-party services
window.addEventListener('unhandledrejection', (event) => {
    // Check if error is from a browser extension or third-party service
    const errorString = event.reason ? event.reason.toString() : '';
    const stackString = event.reason && event.reason.stack ? event.reason.stack.toString() : '';
    const errorMessage = event.reason && event.reason.message ? event.reason.message.toString() : '';
    
    // Combine all error strings for comprehensive checking
    const combinedErrorString = errorString + ' ' + stackString + ' ' + errorMessage;
    
    if (errorString.includes('content.js') || 
        stackString.includes('content.js') ||
        errorString.includes('extension://') ||
        stackString.includes('extension://') ||
        errorString.includes('chrome-extension://') ||
        stackString.includes('chrome-extension://') ||
        errorString.includes('runtime.lastError') ||
        stackString.includes('runtime.lastError') ||
        errorMessage.includes('runtime.lastError') ||
        errorString.includes('rokt') ||
        stackString.includes('rokt') ||
        errorString.includes('Content Security Policy') ||
        stackString.includes('Content Security Policy') ||
        errorString.includes('CSP') ||
        // Check for checkoutUrls errors (common in shopping/browser extensions)
        combinedErrorString.includes('checkoutUrls') ||
        errorString.includes('checkoutUrls') ||
        errorMessage.includes('checkoutUrls') ||
        stackString.includes('checkoutUrls') ||
        (combinedErrorString.includes('checkout') && combinedErrorString.includes('undefined')) ||
        // Check for "Cannot read properties" errors with checkoutUrls (TypeError pattern)
        (combinedErrorString.includes('Cannot read properties') && combinedErrorString.includes('checkoutUrls')) ||
        (errorMessage.includes('Cannot read properties') && errorMessage.includes('checkoutUrls')) ||
        (errorString.includes('Cannot read properties') && errorString.includes('checkoutUrls')) ||
        // Check for "reading 'checkoutUrls'" pattern
        combinedErrorString.includes("reading 'checkoutUrls'") ||
        errorMessage.includes("reading 'checkoutUrls'") ||
        errorString.includes("reading 'checkoutUrls'") ||
        // Check for message channel errors (Chrome extension messaging)
        combinedErrorString.includes('message channel') ||
        combinedErrorString.includes('message channel closed') ||
        combinedErrorString.includes('asynchronous response')) {
        // Prevent extension and third-party service errors from being logged
        event.preventDefault();
        return;
    }
    // Allow other promise rejections to be handled normally
});

// Suppress Chrome extension runtime.lastError messages
if (typeof chrome !== 'undefined' && chrome.runtime) {
    const originalLastError = chrome.runtime.lastError;
    Object.defineProperty(chrome.runtime, 'lastError', {
        get: function() {
            const error = originalLastError;
            // Clear the error to prevent it from being logged
            if (error) {
                return null;
            }
            return error;
        },
        configurable: true
    });
}

// Suppress console errors from Chrome extension message channel issues
if (typeof console !== 'undefined' && console.error) {
    const originalConsoleError = console.error;
    console.error = function(...args) {
        // Filter out Chrome extension message channel errors
        const errorMessage = args.join(' ');
        if (errorMessage.includes('Unchecked runtime.lastError') ||
            errorMessage.includes('message channel closed') ||
            errorMessage.includes('asynchronous response') ||
            errorMessage.includes('checkoutUrls') ||
            (errorMessage.includes('Cannot read properties') && errorMessage.includes('checkoutUrls')) ||
            errorMessage.includes("reading 'checkoutUrls'") ||
            errorMessage.includes('content.js')) {
            // Silently ignore these extension errors
            return;
        }
        // Call original console.error for other errors
        originalConsoleError.apply(console, args);
    };
}

// Global application state
let appInitialized = false;

// German text is now directly embedded in HTML

// Translation system removed - German text is now directly embedded in HTML

/**
* Initialize contact form functionality
* 
* This function sets up the contact form with proper event handling, validation,
* and submission logic. It includes comprehensive error handling and user feedback
* through notifications. The form validates user consent, email format, and
* handles both successful submissions and error cases.
* 
* Features:
* - Form validation (consent and email format)
* - Loading state management
* - Error handling with user-friendly messages
* - Success feedback
* - Form reset after successful submission
* 
* @returns {void} No return value
*/
function initializeContactForm()
{
    const contactForm = document.getElementById('contactForm');
    if (contactForm)
    {
        contactForm.addEventListener('submit', async (e) =>
        {
            e.preventDefault();

            try
            {
                const formData = new FormData(contactForm);
                const name = formData.get('name');
                const email = formData.get('email');
                const inquiry = formData.get('inquiry');
                const consent = formData.get('consent');

                // Validate user consent for data processing
                if (!consent)
                {
                    if (window.UIUtils)
                    {
                        window.UIUtils.showNotification('Bitte stimmen Sie der Datenverarbeitung zu.', 'warning');
                    }
                    return;
                }

                // Validate email format using regex
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) 
                {
                    if (window.UIUtils)
                    {
                        window.UIUtils.showNotification('Bitte geben Sie eine gültige E-Mail-Adresse ein.', 'warning');
                    }
                    return;
                }

                // Update submit button to show loading state
                const submitBtn = document.getElementById('formSubmitBtn');
                if (!submitBtn)
                {
                    console.error('Submit button not found');
                    return;
                }
                const originalText = submitBtn.textContent;
                submitBtn.textContent = 'Wird gesendet...';
                submitBtn.disabled = true;

                try
                {
                    // Track form interaction - get sessionId from analytics client
                    const sessionId = window.AnalyticsClient?.sessionId || localStorage.getItem('analytics_session_id') || null;
                    if (sessionId) {
                        try {
                            fetch('/api/analytics/form-interaction', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    sessionId: sessionId,
                                    formId: 'contactForm',
                                    formName: 'Contact Form',
                                    fieldName: 'submit',
                                    fieldType: 'submit',
                                    actionType: 'submit',
                                    pagePath: window.location.pathname
                                })
                            }).catch(() => {}); // Silently fail if analytics is unavailable
                        } catch (e) {
                            // Silently fail if analytics tracking fails
                        }
                    }

                    // Send form data to server
                    const response = await fetch('/api/contact', 
                    {
                        method: 'POST',
                        headers: 
                        {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(
                        {
                            name: name,
                            email: email,
                            inquiry: inquiry,
                            consent: consent
                        })
                    });

                    const result = await response.json();

                    if (response.ok)
                    {
                        // Reset form and show success message
                        contactForm.reset();
                        const successMessage = result.message || 'Ihre Nachricht wurde erfolgreich gesendet! Wir werden uns bald bei Ihnen melden.';
                        if (window.UIUtils)
                        {
                            window.UIUtils.showNotification(successMessage, 'success');
                        }
                    }
                    else
                    {
                        // Show error message for failed submission
                        const errorMessage = 'Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.';
                        if (window.UIUtils)
                        {
                            window.UIUtils.showNotification(errorMessage, 'error');
                        }
                    }
                }
                catch (fetchError)
                {
                    // Handle network or fetch errors
                    const errorMessage = 'Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.';
                    if (window.UIUtils)
                    {
                        window.UIUtils.showNotification(errorMessage, 'error');
                    }
                } 
                finally 
                {
                    // Always restore submit button state
                    if (submitBtn)
                    {
                        submitBtn.textContent = originalText;
                        submitBtn.disabled = false;
                    }
                }
            }
            catch (error)
            {
                // Handle unexpected errors
                console.error('Contact form error:', error);
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Ein unerwarteter Fehler ist aufgetreten.', 'error');
                }
            }
        });
    }
}

/**
* Initialize order history functionality for regular users
* 
* This function retrieves and displays the order history for the currently logged-in user.
* It checks for authentication, fetches orders from the server, and displays them in a modal.
* The function handles authentication errors and network failures gracefully.
* 
* Features:
* - Authentication validation
* - Server communication with proper headers
* - Error handling for network and authentication issues
* - Modal display of order history
* 
* @returns {Promise<void>} Promise that resolves when order history is loaded
*/
async function showOrderHistory()
{
    const currentUser = window.AccountManagement ? window.AccountManagement.getCurrentUser() : null;
    const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;

    // Check if user is authenticated
    if (!currentUser || !authToken)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Bitte melden Sie sich zuerst an', 'warning');
        }
        return;
    }

    try
    {
        // Fetch user's orders from server
        const response = await fetch('/api/orders', 
        {
            headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (!response.ok)
        {
            throw new Error('Fehler beim Laden der Bestellhistorie');
        }

        const data = await response.json();
        showOrderHistoryModal(data.orders);
    }
    catch (error)
    {
        console.error('Fehler beim Laden der Bestellhistorie:', error);
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Fehler beim Laden der Bestellhistorie', 'error');
        }
    }
}

/**
* Show order history modal with user's orders
* 
* This function creates and displays a modal containing the user's order history.
* Each order is displayed with its details, status, and action buttons for viewing
* details or downloading invoices. The modal is responsive and includes proper
* styling for a professional appearance.
* 
* Features:
* - Responsive modal design
* - Order details display (ID, date, status, total, PayPal ID)
* - Action buttons for each order (view details, download/generate invoice)
* - Empty state handling when no orders exist
* - Scrollable content area for many orders
* 
* @param {Array} orders - Array of user order objects containing order details
* @returns {void} No return value
*/
function showOrderHistoryModal(orders)
{
    const modal = document.createElement('div');
    modal.id = 'orderHistoryModal';
    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.8);
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
    background: white;
    border-radius: 15px;
    padding: 2rem;
    max-width: 800px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    `;

    // Generate HTML for each order or show empty state
    const ordersHTML = orders.length > 0 ? orders.map(order => `
    <div style="background: #f8f9fa; padding: 1rem; margin-bottom: 1rem; border-radius: 8px; border-left: 4px solid #28a745;">
    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
    <div style="flex: 1;">
    <h4 style="margin: 0 0 0.5rem 0; color: #333;">Bestellung #${order.id}</h4>
    <p style="margin: 0.25rem 0; color: #666;"><strong>Datum:</strong> ${new Date(order.date).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' })}</p>
    <p style="margin: 0.25rem 0; color: #666;"><strong>Status:</strong> ${order.status}</p>
    <p style="margin: 0.25rem 0; color: #666;"><strong>Gesamt:</strong> €${order.total.toFixed(2)}</p>
    ${order.paypal_order_id ? `<p style="margin: 0.25rem 0; color: #666;"><strong>PayPal ID:</strong> ${order.paypal_order_id}</p>` : ''}
    </div>
    <div style="display: flex; gap: 0.5rem; flex-direction: column;">
    <button onclick="viewOrderDetails(${order.id})" style="background: #17a2b8; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">👁️ Details</button>
    ${order.has_invoice ? 
        `<button onclick="downloadUserOrderInvoice(${order.id})" style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">📄 Rechnung</button>` :
        `<button onclick="generateUserOrderInvoice(${order.id})" style="background: #ffc107; color: #212529; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">📄 Rechnung erstellen</button>`
    }
    </div>
    </div>
    </div>
    `).join('') : '<p style="text-align: center; color: #666;">Keine Bestellungen gefunden.</p>';

    modalContent.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
    <h2 style="color: #667eea; margin: 0;">📋 Meine Bestellungen</h2>
    </div>
    <div style="max-height: 400px; overflow-y: auto;">
    ${ordersHTML}
    </div>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);
}

/**
* Show car details management interface
* 
* This function retrieves and displays the user's car details in a modal interface.
* It checks for authentication, fetches car data from the server, and displays
* the information in an editable form. If no car data exists, it shows an empty form.
* 
* Features:
* - Authentication validation
* - Server communication for car data retrieval
* - Modal display with editable form
* - Error handling with fallback to empty form
* 
* @returns {Promise<void>} Promise that resolves when car details are loaded
*/
async function showCarDetails()
{
    const currentUser = window.AccountManagement ? window.AccountManagement.getCurrentUser() : null;
    const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;

    // Check if user is authenticated
    if (!currentUser || !authToken)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Bitte melden Sie sich zuerst an', 'warning');
        }
        return;
    }

    try
    {
        // Fetch user's car details from server
        const response = await fetch('/api/car-details', 
        {
            headers: { 'Authorization': `Bearer ${authToken}` }
        });

        const carData = await response.json();
        showCarDetailsModal(carData);
    }
    catch (error)
    {
        console.error('Fehler beim Laden der Fahrzeugdaten:', error);
        // Show empty form if data loading fails
        showCarDetailsModal({});
    }
}

/**
* Show car details modal
* @param {Object} carData - Car data
*/
function showCarDetailsModal(carData)
{
    const modal = document.createElement('div');
    modal.id = 'carDetailsModal';
    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.8);
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
    background: white;
    border-radius: 15px;
    padding: 2rem;
    max-width: 600px;
    width: 90%;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    `;

    modalContent.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
    <h2 style="color: #667eea; margin: 0;">🚗 Fahrzeugdaten</h2>
    </div>
    <form onsubmit="saveCarDetails(event)">
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333;">Marke:</label>
    <input type="text" id="carMake" value="${carData.make || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333;">Modell:</label>
    <input type="text" id="carModel" value="${carData.model || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333;">Baujahr:</label>
    <input type="number" id="carYear" value="${carData.year || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333;">Motor:</label>
    <input type="text" id="carEngine" value="${carData.engine || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333;">Leistung (PS):</label>
    <input type="number" id="carPower" value="${carData.power || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="margin-bottom: 1.5rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333;">VIN (Fahrgestellnummer):</label>
    <input type="text" id="carVin" value="${carData.vin || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <button type="submit" style="width: 100%; background: #28a745; color: white; border: none; padding: 12px; border-radius: 8px; cursor: pointer;">Fahrzeugdaten speichern</button>
    </form>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    const closeModal = () =>
    {
        if (modal.parentNode)
        {
            modal.parentNode.removeChild(modal);
        }
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            window.UIUtils.updateGlobalCloseButton();
        }
    };
    modal._closeModal = closeModal;
    if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
    {
        requestAnimationFrame(() => window.UIUtils.updateGlobalCloseButton());
    }
}

/**
* Save car details
* @param {Event} event - Form submit event
*/
async function saveCarDetails(event)
{
    event.preventDefault();

    const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
    if (!authToken)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Keine Berechtigung', 'error');
        }
        return;
    }

    const carData = {
        make: document.getElementById('carMake').value,
        model: document.getElementById('carModel').value,
        year: parseInt(document.getElementById('carYear').value) || null,
        engine: document.getElementById('carEngine').value,
        power: parseInt(document.getElementById('carPower').value) || null,
        vin: document.getElementById('carVin').value
    };

    try
    {
        const response = await fetch('/api/car-details', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(carData)
        });

        if (response.ok)
        {
            if (window.UIUtils && window.UIUtils.safeCloseModal)
            {
                window.UIUtils.safeCloseModal('carDetailsModal');
            }
            else
            {
                document.getElementById('carDetailsModal').remove();
            }
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Fahrzeugdaten erfolgreich gespeichert!', 'success');
            }
        }
        else
        {
            const errorData = await response.json();
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Fehler beim Speichern: ' + (errorData.error || 'Unbekannter Fehler'), 'error');
            }
        }
    }
    catch (error)
    {
        console.error('Fehler beim Speichern der Fahrzeugdaten:', error);
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Fehler beim Speichern der Fahrzeugdaten', 'error');
        }
    }
}

/**
* Download user order invoice
* @param {number} orderId - Order ID
*/
async function downloadUserOrderInvoice(orderId)
{
    try
    {
        const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
        if (!authToken)
        {
            throw new Error('Keine Berechtigung');
        }

        const response = await fetch(`/api/orders/${orderId}/invoice`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (response.ok)
        {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;

            // Get filename from response headers (should match database invoice_filename)
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `Rechnung_${orderId}.pdf`; // Fallback
            if (contentDisposition)
            {
                const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                if (filenameMatch)
                {
                    filename = filenameMatch[1]; // Use exact filename from database
                }
            }

            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Rechnung erfolgreich heruntergeladen!', 'success');
            }
        }
        else
        {
            const errorData = await response.json();
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Fehler beim Herunterladen der Rechnung: ' + (errorData.error || 'Unbekannter Fehler'), 'error');
            }
        }
    }
    catch (error)
    {
        console.error('Fehler beim Herunterladen der Rechnung:', error);
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Fehler beim Herunterladen der Rechnung', 'error');
        }
    }
}

/**
* Generate user order invoice
* @param {number} orderId - Order ID
*/
async function generateUserOrderInvoice(orderId)
{
    try
    {
        const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
        if (!authToken)
        {
            throw new Error('Keine Berechtigung');
        }

        // Get order details first
        const orderResponse = await fetch(`/api/orders/${orderId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (!orderResponse.ok)
        {
            throw new Error('Bestellung nicht gefunden');
        }

        const orderData = await orderResponse.json();
        const order = orderData.order;

        // Generate invoice using the invoice generation module
        if (window.InvoiceGeneration)
        {
            window.InvoiceGeneration.generateInvoice(
            order.paypal_order_id || 'ORDER-' + orderId,
            order.status,
            order.total,
            order.items
            );
        }
    }
    catch (error)
    {
        console.error('Fehler beim Erstellen der Rechnung:', error);
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Fehler beim Erstellen der Rechnung', 'error');
        }
    }
}

/**
* View order details for regular users
* @param {number} orderId - Order ID
*/
async function viewOrderDetails(orderId)
{
    try
    {
        const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
        if (!authToken)
        {
            throw new Error('Keine Berechtigung');
        }

        const response = await fetch(`/api/orders/${orderId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (!response.ok)
        {
            throw new Error('Bestellung nicht gefunden');
        }

        const data = await response.json();
        showUserOrderDetailsModal(data.order);
    }
    catch (error)
    {
        console.error('Fehler beim Laden der Bestelldetails:', error);
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Fehler beim Laden der Bestelldetails', 'error');
        }
    }
}

/**
* Show user order details modal
* @param {Object} order - Order data
*/
function showUserOrderDetailsModal(order)
{
    const modal = document.createElement('div');
    modal.id = 'userOrderDetailsModal';
    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.8);
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
    background: white;
    border-radius: 15px;
    padding: 2rem;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    `;

    const items = JSON.parse(order.items || '[]');
    const itemsHTML = items.map(item => `
    <div style="background: #f8f9fa; padding: 0.5rem; margin-bottom: 0.5rem; border-radius: 4px;">
    <strong>${item.name}</strong> - €${item.price.toFixed(2)} × ${item.quantity}
    </div>
    `).join('');

    modalContent.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
    <h2 style="color: #667eea; margin: 0;">Bestellung #${order.id}</h2>
    </div>
    <div style="margin-bottom: 1rem;">
    <h3 style="color: #333; margin-bottom: 0.5rem;">Bestellinformationen</h3>
    <p><strong>Datum:</strong> ${new Date(order.date).toLocaleDateString('de-DE')}</p>
    <p><strong>Status:</strong> ${order.status}</p>
    <p><strong>Gesamt:</strong> €${order.total.toFixed(2)}</p>
    ${order.paypal_order_id ? `<p><strong>PayPal ID:</strong> ${order.paypal_order_id}</p>` : ''}
    </div>
    <div>
    <h3 style="color: #333; margin-bottom: 0.5rem;">Artikel</h3>
    ${itemsHTML || '<p>Keine Artikel gefunden</p>'}
    </div>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    const closeModal = () =>
    {
        if (modal.parentNode)
        {
            modal.parentNode.removeChild(modal);
        }
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            window.UIUtils.updateGlobalCloseButton();
        }
    };
    modal._closeModal = closeModal;
    if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
    {
        requestAnimationFrame(() => window.UIUtils.updateGlobalCloseButton());
    }
}

/**
* Initialize the application with all modules and dependencies
* 
* This is the main initialization function that sets up the entire application.
* It applies translations, initializes all modules in the correct order, and
* sets up global functions for HTML onclick handlers. The function uses
* asynchronous loading for better performance and handles module dependencies.
* 
* Features:
* - Prevents multiple initializations
* - Applies German translations first
* - Asynchronous module loading with proper timing
* - Global function setup for HTML event handlers
* - Error handling and logging
* - Performance optimization with requestAnimationFrame
* 
* @returns {Promise<void>} Promise that resolves when application is fully initialized
*/
async function initializeApp()
{
    // Prevent multiple initializations
    if (appInitialized)
    {
        return;
    }

    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Initializing application...');

    try
    {
        // Set language attribute
        document.documentElement.lang = 'de';

        // Initialize modules asynchronously for better performance
        const initPromises = [];

        // UI Utils (essential - load first)
        if (window.UIUtils)
        {
            initPromises.push(new Promise((resolve) =>
            {
                requestAnimationFrame(() =>
                {
                    window.UIUtils.initializeUIUtils();
                    resolve();
                });
            }));
        }

        // Contact form (essential)
        initPromises.push(new Promise((resolve) =>
        {
            requestAnimationFrame(() =>
            {
                initializeContactForm();
                resolve();
            });
        }));

        // Shopping Cart (important but can load async)
        if (window.ShoppingCart)
        {
            initPromises.push(new Promise((resolve) =>
            {
                setTimeout(() =>
                {
                    window.ShoppingCart.initializeShoppingCart();
                    resolve();
                }, 100);
            }));
        }

        // Account Management (important but can load async)
        if (window.AccountManagement)
        {
            initPromises.push(new Promise((resolve) =>
            {
                setTimeout(() =>
                {
                    window.AccountManagement.initializeAccountManagement();
                    resolve();
                }, 200);
            }));
        }

        // PayPal (only when needed)
        if (window.PayPalIntegration)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal integration module loaded lazily');
        }

        // Wait for critical modules to initialize
        await Promise.all(initPromises.slice(0, 2));

        // Add global functions to window for HTML onclick handlers
        window.toggleUserMenu = window.AccountManagement ? window.AccountManagement.toggleUserMenu : () =>
        {};
        window.toggleCart = window.ShoppingCart ? window.ShoppingCart.toggleCart : () =>
        {};
        window.addToCart = window.ShoppingCart ? window.ShoppingCart.addToCart : () =>
        {};

        // handleNavClick should already be available from ui-utils.js
        if (window.handleNavClick && !window.handleNavClick.toString().includes('FALLBACK')) {
        }
        else
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('⚠️ handleNavClick not properly loaded, using fallback');
        }
        window.scrollToContact = window.UIUtils ? window.UIUtils.scrollToContact : () =>
        {};
        window.showUserDashboard = window.AccountManagement ? window.AccountManagement.showUserDashboard : () =>
        {};
        window.logout = window.AccountManagement ? window.AccountManagement.logout : () =>
        {};
        window.closeModal = window.UIUtils ? window.UIUtils.closeModal : () =>
        {};
        window.showPrivacyPolicy = window.UIUtils ? window.UIUtils.showPrivacyPolicy : () =>
        {};
        window.showImpressum = window.UIUtils ? window.UIUtils.showImpressum : () =>
        {};
        window.showWiderruf = window.UIUtils ? window.UIUtils.showWiderruf : () =>
        {};
        window.switchDashboardTab = window.AccountManagement ? window.AccountManagement.switchDashboardTab : () =>
        {};
        // Make showImagePreview available globally (if defined)
        window.showImagePreview = typeof showImagePreview !== 'undefined' ? showImagePreview : (window.showImagePreview || function() { console.warn('showImagePreview not available'); });
        window.togglePasswordVisibility = window.AccountManagement ? window.AccountManagement.togglePasswordVisibility : () =>
        {};
        window.handleForgotPassword = window.AccountManagement ? window.AccountManagement.handleForgotPassword : () =>
        {};

        console.log('✅ Global functions set up:', 
        {
            handleNavClick: typeof window.handleNavClick,
            UIUtils: typeof window.UIUtils,
            toggleUserMenu: typeof window.toggleUserMenu
        });

        // Debug: Check if handleNavClick is working
        if (typeof window.handleNavClick === 'function')
        {
        }
        else
        {
            console.error('❌ handleNavClick is not available:', typeof window.handleNavClick);
        }

        // User dashboard functions
        window.showOrderHistory = showOrderHistory;
        window.showCarDetails = showCarDetails;
        window.saveCarDetails = saveCarDetails;
        window.downloadUserOrderInvoice = downloadUserOrderInvoice;
        window.generateUserOrderInvoice = generateUserOrderInvoice;
        window.viewOrderDetails = viewOrderDetails;

        appInitialized = true;
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Application initialized successfully');

    }
    catch (error)
    {
        console.error('Error initializing application:', error);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading')
{
    document.addEventListener('DOMContentLoaded', initializeApp);
}
else
{
    initializeApp();
}

// Export main functions
window.MainApp = {
    initializeApp,
    showOrderHistory,
    showCarDetails,
    saveCarDetails,
    downloadUserOrderInvoice,
    generateUserOrderInvoice,
    viewOrderDetails
};
